import { CONFIG } from '../config/tuning'
import type { RNG } from '../core/rng'
import type { EnemyType } from '../ecs/entity'
import type { GeneratorPlacement, LevelData } from '../world/level'
import { validateLevel } from '../world/level'

/**
 * Procedural floor generator. Emits the SAME frozen LevelData schema as the
 * authored levels — nothing downstream knows or cares where a floor came
 * from. Takes the campaign cadence seriously: act/difficulty band scales
 * enemy budget, generator count and layout complexity, and every 5th floor
 * is a boss gate (data-variant bosses on the shared framework).
 *
 * Standard floors draw one of three layout ARCHETYPES from the level RNG:
 * - CHAIN: scattered rooms joined nearest-neighbor by L-corridors (linear)
 * - HUB:   one large central room with radiating spoke rooms; the exit sits
 *          in the spoke farthest from spawn (occasionally a locked VAULT)
 * - RING:  rooms arranged in a loop with a connecting ring of corridors —
 *          always at least two routes to the exit
 * All archetypes seed door tiles at room/corridor chokepoints so the
 * authored floors' door texture survives into the generated descent.
 */

export type Archetype = 'chain' | 'hub' | 'ring'

/** validator/test hooks only — the game always rolls from the RNG */
export interface GenOptions {
  archetype?: Archetype
  bossLayout?: number
}

interface Room {
  x: number
  y: number
  w: number
  h: number
}

/** a built layout, ready for population (hostiles, pickups, hazards) */
interface Layout {
  grid: string[][]
  rooms: Room[]
  spawnRoom: Room
  exitRoom: Room
  /** rooms hostile placements draw from (never the spawn room) */
  midRooms: Room[]
  /** rooms safe for the key when the exit is locked (reachable pre-lock) */
  keyRooms: Room[]
  /** chance the exit gets locked with the key elsewhere (0 disables) */
  lockChance: number
}

const roomCenter = (r: Room): { x: number; y: number } => ({
  x: Math.floor(r.x + r.w / 2),
  y: Math.floor(r.y + r.h / 2)
})

export function bandForFloor(floor: number): number {
  // authored floors define bands 0..5; procedural floors continue the curve
  return Math.min(9, Math.floor((floor - 1) / 2.5))
}

export function generateLevel(rng: RNG, floor: number, opts?: GenOptions): LevelData {
  const band = bandForFloor(floor)
  const act = Math.ceil(floor / 5)
  const isBossGate = floor % 5 === 0
  return isBossGate ? genBossGate(rng, floor, act, band, opts?.bossLayout) : genStandard(rng, floor, act, band, opts?.archetype)
}

// — standard floors ——————————————————————————————————————————————————

function pickArchetype(rng: RNG): Archetype {
  const p = CONFIG.procgen
  const total = p.archetypeChainWeight + p.archetypeHubWeight + p.archetypeRingWeight
  let r = rng.next() * total
  if ((r -= p.archetypeChainWeight) < 0) return 'chain'
  if ((r -= p.archetypeHubWeight) < 0) return 'hub'
  return 'ring'
}

function genStandard(rng: RNG, floor: number, act: number, band: number, forced?: Archetype): LevelData {
  const archetype = forced ?? pickArchetype(rng)
  const layout = archetype === 'hub' ? buildHub(rng, band) : archetype === 'ring' ? buildRing(rng, band) : buildChain(rng, band)
  const { grid, rooms, spawnRoom, exitRoom, midRooms } = layout
  const H = grid.length
  const W = grid[0]!.length

  const spawn = roomCenter(spawnRoom)
  const exitC = roomCenter(exitRoom)
  grid[exitC.y]![exitC.x] = 'E'

  // — generators: the real objective —
  const genTypes: Exclude<EnemyType, 'auditor'>[] = ['husk']
  if (band >= 1) genTypes.push('sentry')
  if (band >= 2) genTypes.push('static')
  if (band >= 3) genTypes.push('foreman')
  const genCount = Math.min(6, 1 + Math.floor(band / 2) + rng.int(1, 2))
  const generators: GeneratorPlacement[] = []
  const usedCells = new Set<string>([`${spawn.x},${spawn.y}`, `${exitC.x},${exitC.y}`])
  for (let i = 0; i < genCount && midRooms.length > 0; i++) {
    const room = rng.pick(midRooms)
    const cell = freeCell(rng, grid, room, usedCells)
    if (!cell) continue
    generators.push({ type: rng.pick(genTypes), x: cell.x, y: cell.y, budget: 12 + band * 2 })
  }

  // — initial enemies —
  const enemyTypes: Exclude<EnemyType, 'auditor'>[] = ['husk']
  if (band >= 1) enemyTypes.push('sentry')
  if (band >= 2) enemyTypes.push('static')
  const enemies: LevelData['enemies'] = []
  const enemyCount = 3 + band + rng.int(0, 3)
  for (let i = 0; i < enemyCount && midRooms.length > 0; i++) {
    const cell = freeCell(rng, grid, rng.pick(midRooms), usedCells)
    if (cell) enemies.push({ type: rng.pick(enemyTypes), x: cell.x, y: cell.y })
  }

  // — pickups —
  const pickups: LevelData['pickups'] = []
  const foodCount = Math.max(2, 4 - Math.floor(band / 3)) + rng.int(0, 1)
  for (let i = 0; i < foodCount; i++) {
    const cell = freeCell(rng, grid, rng.pick(rooms), usedCells)
    if (cell) pickups.push({ type: 'food', x: cell.x, y: cell.y })
  }
  const treasureCount = rng.int(1, 3)
  for (let i = 0; i < treasureCount; i++) {
    const cell = freeCell(rng, grid, rng.pick(rooms), usedCells)
    if (cell) pickups.push({ type: 'treasure', x: cell.x, y: cell.y })
  }
  if (rng.chance(0.55)) {
    const cell = freeCell(rng, grid, rng.pick(rooms), usedCells)
    if (cell) pickups.push({ type: 'magic', x: cell.x, y: cell.y })
  }
  // — archive fragment: rare, and always in the room farthest from spawn —
  // DECISION: "far room" = greatest center distance from the spawn room, so a
  // generated fragment always costs a detour; the roll consumes RNG whether or
  // not placement succeeds, keeping the stream stable per seed+floor
  if (rng.chance(CONFIG.archive.procgenChance)) {
    let far: Room | null = null
    let fd = -1
    for (const room of rooms) {
      if (room === spawnRoom) continue
      const c = roomCenter(room)
      const d = (c.x - spawn.x) ** 2 + (c.y - spawn.y) ** 2
      if (d > fd) {
        fd = d
        far = room
      }
    }
    const cell = far ? freeCell(rng, grid, far, usedCells) : null
    if (cell) pickups.push({ type: 'archive', x: cell.x, y: cell.y })
  }

  // — optional locked exit + key (the HUB vault, the CHAIN gate) —
  // most floors gate the exit on the assemblers — they are the objective, not scenery
  let exitType: LevelData['exit']['type'] = rng.chance(0.55) ? 'generators' : 'reach'
  if (exitType === 'reach' && rooms.length >= 4 && layout.keyRooms.length > 0 && rng.chance(layout.lockChance)) {
    const doorCell = entryCell(grid, exitRoom)
    const keyRoom = rng.pick(layout.keyRooms)
    const keyCell = freeCell(rng, grid, keyRoom, usedCells)
    if (doorCell && keyCell) {
      grid[doorCell.y]![doorCell.x] = 'L'
      // the lock cell may sit on a shared corridor — if it strands the key
      // behind itself, the vault is unwinnable, so revert to an open floor
      if (openReachable(grid, spawn).has(`${keyCell.x},${keyCell.y}`)) {
        pickups.push({ type: 'key', x: keyCell.x, y: keyCell.y })
      } else {
        grid[doorCell.y]![doorCell.x] = '.'
      }
    }
  }
  if (exitType === 'generators' && generators.length === 0) exitType = 'reach'

  // — doors at room/corridor chokepoints (the authored floors' texture) —
  placeDoors(grid, rooms, rng, usedCells)

  // — hazards & flavor —
  const trapChance = 0.02 + band * 0.008
  for (let y = 1; y < H - 1; y++) {
    for (let x = 1; x < W - 1; x++) {
      if (grid[y]![x] === '.' && rng.chance(trapChance) && !usedCells.has(`${x},${y}`)) {
        // corridors only (cells with exactly 2 open orthogonal neighbors)
        const open = ['0,-1', '0,1', '-1,0', '1,0'].filter((d) => {
          const [dx, dy] = d.split(',').map(Number)
          return grid[y + dy!]?.[x + dx!] !== '#'
        }).length
        if (open === 2) grid[y]![x] = 'T'
      }
    }
  }
  if (rng.chance(0.35) && rooms.length >= 4) {
    const a = freeCell(rng, grid, rng.pick(rooms), usedCells)
    const b = freeCell(rng, grid, rng.pick(rooms), usedCells)
    if (a && b && (a.x !== b.x || a.y !== b.y)) {
      grid[a.y]![a.x] = '1'
      grid[b.y]![b.x] = '1'
    }
  }
  // a destructible pocket with loot
  if (rng.chance(0.4)) pocket(grid, rng, pickups)

  const level: LevelData = {
    id: `p${floor}`,
    name: procName(rng, floor),
    floor,
    act,
    band,
    isBossGate: false,
    tiles: grid.map((r) => r.join('')),
    generators,
    enemies,
    pickups,
    playerSpawns: [spawn],
    exit: { type: exitType },
    ...(floor >= 4
      ? { auditorSpawns: [{ delay: Math.max(30, 85 - band * 6) }, ...(band >= 6 && rng.chance(0.5) ? [{ delay: 140 }] : [])] }
      : {})
  }
  validateLevel(level)
  return level
}

// — archetype builders ————————————————————————————————————————————————

function emptyGrid(W: number, H: number): string[][] {
  return Array.from({ length: H }, () => Array.from({ length: W }, () => '#'))
}

/** CHAIN: scattered rooms joined nearest-neighbor with L-corridors. */
function buildChain(rng: RNG, band: number): Layout {
  const W = Math.min(46, 28 + band * 2 + rng.int(0, 4))
  const H = Math.min(30, 16 + band + rng.int(0, 4))
  const grid = emptyGrid(W, H)

  const roomTarget = 5 + Math.min(5, Math.floor(band / 2)) + rng.int(0, 2)
  const rooms: Room[] = []
  for (let tries = 0; tries < 80 && rooms.length < roomTarget; tries++) {
    const w = rng.int(5, 9)
    const h = rng.int(4, 7)
    const x = rng.int(1, W - w - 2)
    const y = rng.int(1, H - h - 2)
    const cand = { x, y, w, h }
    if (rooms.some((r) => overlap(r, cand, 1))) continue
    rooms.push(cand)
    carve(grid, cand)
  }
  // guarantee a minimum layout
  if (rooms.length < 3) {
    for (const r of [
      { x: 2, y: 2, w: 6, h: 5 },
      { x: W - 9, y: H - 8, w: 6, h: 5 },
      { x: Math.floor(W / 2) - 3, y: Math.floor(H / 2) - 2, w: 6, h: 5 }
    ]) {
      rooms.push(r)
      carve(grid, r)
    }
  }

  const chain = chainRooms(rooms)
  for (let i = 1; i < chain.length; i++) {
    corridor(grid, roomCenter(chain[i - 1]!), roomCenter(chain[i]!), rng)
  }

  return {
    grid,
    rooms: chain,
    spawnRoom: chain[0]!,
    exitRoom: chain[chain.length - 1]!,
    midRooms: chain.slice(1, -1),
    keyRooms: chain.slice(0, Math.max(1, chain.length - 2)),
    lockChance: 0.25
  }
}

/** HUB: one large central room, 4-6 spokes on radiating corridors. */
function buildHub(rng: RNG, band: number): Layout {
  const W = Math.min(46, 34 + band + rng.int(0, 4))
  const H = Math.min(30, 20 + Math.floor(band / 2) + rng.int(0, 3))
  const grid = emptyGrid(W, H)

  const hw = rng.int(8, 11)
  const hh = rng.int(5, 7)
  const hub: Room = { x: Math.floor((W - hw) / 2), y: Math.floor((H - hh) / 2), w: hw, h: hh }
  carve(grid, hub)

  const spokes: Room[] = []
  const spokeTarget = rng.int(4, 6)
  for (let i = 0; i < spokeTarget; i++) {
    const ang = (i / spokeTarget) * Math.PI * 2 + rng.range(-0.35, 0.35)
    const w = rng.int(5, 7)
    const h = rng.int(4, 5)
    // walk the spoke inward until it fits clear of the hub and its siblings
    for (const scale of [1, 0.82, 0.66]) {
      const cx = W / 2 + Math.cos(ang) * (W / 2 - w / 2 - 2) * scale
      const cy = H / 2 + Math.sin(ang) * (H / 2 - h / 2 - 2) * scale
      const x = Math.max(1, Math.min(W - w - 2, Math.round(cx - w / 2)))
      const y = Math.max(1, Math.min(H - h - 2, Math.round(cy - h / 2)))
      const cand = { x, y, w, h }
      if (overlap(hub, cand, 1) || spokes.some((r) => overlap(r, cand, 1))) continue
      spokes.push(cand)
      carve(grid, cand)
      break
    }
  }
  // guarantee: corner spokes if the radial placement came up short
  if (spokes.length < 4) {
    for (const cand of [
      { x: 2, y: 2, w: 6, h: 4 },
      { x: W - 9, y: 2, w: 6, h: 4 },
      { x: 2, y: H - 7, w: 6, h: 4 },
      { x: W - 9, y: H - 7, w: 6, h: 4 }
    ]) {
      if (spokes.length >= 4) break
      if (overlap(hub, cand, 0) || spokes.some((r) => overlap(r, cand, 0))) continue
      spokes.push(cand)
      carve(grid, cand)
    }
  }

  const hc = roomCenter(hub)
  for (const s of spokes) corridor(grid, hc, roomCenter(s), rng)

  // spawn and exit in the pair of spokes farthest apart
  let spawnRoom = spokes[0]!
  let exitRoom = spokes[spokes.length - 1]!
  let bestD = -1
  for (const a of spokes) {
    for (const b of spokes) {
      const ca = roomCenter(a)
      const cb = roomCenter(b)
      const d = (ca.x - cb.x) ** 2 + (ca.y - cb.y) ** 2
      if (d > bestD) {
        bestD = d
        spawnRoom = a
        exitRoom = b
      }
    }
  }
  const others = spokes.filter((s) => s !== spawnRoom && s !== exitRoom)
  return {
    grid,
    rooms: [hub, ...spokes],
    spawnRoom,
    exitRoom,
    midRooms: [hub, ...others],
    keyRooms: others.length > 0 ? others : [hub],
    lockChance: CONFIG.procgen.vaultChance
  }
}

/** RING: rooms on a loop — always at least two routes to the exit. */
function buildRing(rng: RNG, band: number): Layout {
  const W = Math.min(46, 34 + band + rng.int(0, 4))
  const H = Math.min(30, 20 + Math.floor(band / 2) + rng.int(0, 3))
  const grid = emptyGrid(W, H)

  const n = rng.int(5, 6)
  const rooms: Room[] = []
  for (let i = 0; i < n; i++) {
    const ang = (i / n) * Math.PI * 2 - Math.PI / 2 + rng.range(-0.22, 0.22)
    const w = rng.int(5, 7)
    const h = rng.int(4, 5)
    const cx = W / 2 + (Math.cos(ang) * (W - w)) / 2 - Math.cos(ang) * 3
    const cy = H / 2 + (Math.sin(ang) * (H - h)) / 2 - Math.sin(ang) * 2.5
    const x = Math.max(1, Math.min(W - w - 2, Math.round(cx - w / 2)))
    const y = Math.max(1, Math.min(H - h - 2, Math.round(cy - h / 2)))
    const room = { x, y, w, h }
    rooms.push(room)
    carve(grid, room)
  }
  // the loop: each room corridors to the next, last wraps to first
  for (let i = 0; i < n; i++) {
    corridor(grid, roomCenter(rooms[i]!), roomCenter(rooms[(i + 1) % n]!), rng)
  }

  const spawnRoom = rooms[0]!
  const exitRoom = rooms[Math.floor(n / 2)]! // opposite side: two ways around
  return {
    grid,
    rooms,
    spawnRoom,
    exitRoom,
    midRooms: rooms.filter((r) => r !== spawnRoom),
    keyRooms: [], // a single lock cannot gate a loop — no vaults on rings
    lockChance: 0
  }
}

// — boss gates ————————————————————————————————————————————————————————

function genBossGate(rng: RNG, floor: number, act: number, band: number, layoutOverride?: number): LevelData {
  const W = 34 + Math.min(8, act * 2)
  const H = 15 + Math.min(6, act)
  const grid: string[][] = Array.from({ length: H }, (_, y) =>
    Array.from({ length: W }, (_, x) => (x === 0 || y === 0 || x === W - 1 || y === H - 1 ? '#' : '.'))
  )
  const block = (cx: number, cy: number): void => {
    for (let dy = 0; dy < 2; dy++) {
      for (let dx = 0; dx < 2; dx++) {
        if (cy + dy > 0 && cy + dy < H - 1 && cx + dx > 0 && cx + dx < W - 1) grid[cy + dy]![cx + dx] = '#'
      }
    }
  }
  const layout = layoutOverride ?? rng.int(0, 2)
  if (layout === 0) {
    // classic quad blocks for dodge cover
    for (const cx of [Math.floor(W * 0.3), Math.floor(W * 0.7)]) {
      for (const cy of [Math.floor(H * 0.3), Math.floor(H * 0.7)]) block(cx, cy)
    }
  } else if (layout === 1) {
    // diagonal pillar run (center left open for the boss)
    for (const f of [0.22, 0.4, 0.6, 0.78]) block(Math.floor(W * f), Math.floor(H * f))
  } else {
    // perimeter notches: stubs jutting in from the walls, open center
    for (const fx of [0.3, 0.7]) {
      const cx = Math.floor(W * fx)
      for (let d = 1; d <= 2; d++) {
        grid[d]![cx] = '#'
        grid[d]![cx + 1] = '#'
        grid[H - 1 - d]![cx] = '#'
        grid[H - 1 - d]![cx + 1] = '#'
      }
    }
    const my = Math.floor(H / 2)
    for (let d = 1; d <= 2; d++) {
      grid[my]![d] = '#'
      grid[my + 1]![d] = '#'
      grid[my]![W - 1 - d] = '#'
      grid[my + 1]![W - 1 - d] = '#'
    }
  }
  const spawn = { x: 3, y: 2 }
  const exitC = { x: Math.floor(W / 2), y: H - 3 }
  grid[exitC.y]![exitC.x] = 'E'
  const bossIds = ['warden', 'overseer', 'director']
  const bossId = bossIds[Math.min(2, act - 1)]!
  const pickups: LevelData['pickups'] = [
    { type: 'food', x: 2, y: H - 3 },
    { type: 'food', x: W - 3, y: 2 },
    { type: 'magic', x: W - 3, y: H - 3 }
  ]
  const level: LevelData = {
    id: `p${floor}b`,
    name: `${procName(rng, floor)} GATE`,
    floor,
    act,
    band,
    isBossGate: true,
    tiles: grid.map((r) => r.join('')),
    generators: [],
    enemies: [],
    pickups,
    playerSpawns: [spawn],
    exit: { type: 'boss' },
    boss: { id: bossId, x: Math.floor(W / 2), y: Math.floor(H / 2) }
  }
  validateLevel(level)
  return level
}

// — helpers ————————————————————————————————————————————————————————————

function overlap(a: Room, b: Room, pad: number): boolean {
  return a.x - pad < b.x + b.w && a.x + a.w + pad > b.x && a.y - pad < b.y + b.h && a.y + a.h + pad > b.y
}

function carve(grid: string[][], r: Room): void {
  for (let y = r.y; y < r.y + r.h; y++) {
    for (let x = r.x; x < r.x + r.w; x++) grid[y]![x] = '.'
  }
}

function chainRooms(rooms: Room[]): Room[] {
  const rest = [...rooms]
  const chain = [rest.shift()!]
  while (rest.length) {
    const last = roomCenter(chain[chain.length - 1]!)
    let bi = 0
    let bd = Infinity
    rest.forEach((r, i) => {
      const c = roomCenter(r)
      const d = (c.x - last.x) ** 2 + (c.y - last.y) ** 2
      if (d < bd) {
        bd = d
        bi = i
      }
    })
    chain.push(rest.splice(bi, 1)[0]!)
  }
  return chain
}

function corridor(grid: string[][], a: { x: number; y: number }, b: { x: number; y: number }, rng: RNG): void {
  const horizFirst = rng.chance(0.5)
  const carveCell = (x: number, y: number) => {
    if (grid[y]?.[x] === '#') grid[y]![x] = '.'
  }
  if (horizFirst) {
    for (let x = Math.min(a.x, b.x); x <= Math.max(a.x, b.x); x++) carveCell(x, a.y)
    for (let y = Math.min(a.y, b.y); y <= Math.max(a.y, b.y); y++) carveCell(b.x, y)
  } else {
    for (let y = Math.min(a.y, b.y); y <= Math.max(a.y, b.y); y++) carveCell(a.x, y)
    for (let x = Math.min(a.x, b.x); x <= Math.max(a.x, b.x); x++) carveCell(x, b.y)
  }
}

/**
 * Door tiles at room/corridor junction chokepoints: a corridor cell on the
 * ring just outside a room, walled on both flanks so it reads as a doorway.
 * Doors are passable to solvability (they open on approach), so BFS holds.
 */
function placeDoors(grid: string[][], rooms: Room[], rng: RNG, used: Set<string>): void {
  const doorChance = CONFIG.procgen.doorChance
  const tryDoor = (x: number, y: number, vertical: boolean): void => {
    if (grid[y]?.[x] !== '.') return
    if (used.has(`${x},${y}`)) return
    const flankA = vertical ? grid[y]?.[x - 1] : grid[y - 1]?.[x]
    const flankB = vertical ? grid[y]?.[x + 1] : grid[y + 1]?.[x]
    if (flankA !== '#' || flankB !== '#') return
    if (!rng.chance(doorChance)) return
    grid[y]![x] = 'D'
  }
  for (const room of rooms) {
    for (let x = room.x; x < room.x + room.w; x++) {
      tryDoor(x, room.y - 1, true)
      tryDoor(x, room.y + room.h, true)
    }
    for (let y = room.y; y < room.y + room.h; y++) {
      tryDoor(room.x - 1, y, false)
      tryDoor(room.x + room.w, y, false)
    }
  }
}

/**
 * BFS flood over open cells, treating locked doors as walls — used to prove a
 * key stays reachable before its lock. Runs pre-hazard, so only '.'/'E'
 * (and any 'L') exist on the grid; anything but '#' and 'L' is passable.
 */
function openReachable(grid: string[][], from: { x: number; y: number }): Set<string> {
  const seen = new Set<string>([`${from.x},${from.y}`])
  const queue: [number, number][] = [[from.x, from.y]]
  while (queue.length) {
    const [x, y] = queue.pop()!
    for (const [dx, dy] of [
      [0, -1],
      [0, 1],
      [-1, 0],
      [1, 0]
    ] as const) {
      const nx = x + dx
      const ny = y + dy
      const c = grid[ny]?.[nx]
      if (c === undefined || c === '#' || c === 'L' || seen.has(`${nx},${ny}`)) continue
      seen.add(`${nx},${ny}`)
      queue.push([nx, ny])
    }
  }
  return seen
}

function freeCell(rng: RNG, grid: string[][], room: Room, used: Set<string>): { x: number; y: number } | null {
  for (let i = 0; i < 20; i++) {
    const x = rng.int(room.x, room.x + room.w - 1)
    const y = rng.int(room.y, room.y + room.h - 1)
    if (grid[y]?.[x] === '.' && !used.has(`${x},${y}`)) {
      used.add(`${x},${y}`)
      return { x, y }
    }
  }
  return null
}

/** first floor cell on the room's boundary ring that leads outward (a choke) */
function entryCell(grid: string[][], room: Room): { x: number; y: number } | null {
  for (let x = room.x; x < room.x + room.w; x++) {
    if (grid[room.y - 1]?.[x] === '.') return { x, y: room.y - 1 }
    if (grid[room.y + room.h]?.[x] === '.') return { x, y: room.y + room.h }
  }
  for (let y = room.y; y < room.y + room.h; y++) {
    if (grid[y]?.[room.x - 1] === '.') return { x: room.x - 1, y }
    if (grid[y]?.[room.x + room.w] === '.') return { x: room.x + room.w, y }
  }
  return null
}

/** small sealed destructible pocket with treasure inside */
function pocket(grid: string[][], rng: RNG, pickups: LevelData['pickups']): void {
  const H = grid.length
  const W = grid[0]!.length
  for (let tries = 0; tries < 30; tries++) {
    const x = rng.int(2, W - 4)
    const y = rng.int(2, H - 4)
    // need a 3x3 solid block with one face adjacent to floor
    let solid = true
    for (let dy = 0; dy < 3; dy++) {
      for (let dx = 0; dx < 3; dx++) {
        if (grid[y + dy]?.[x + dx] !== '#') solid = false
      }
    }
    if (!solid) continue
    const faces = [
      { fx: x + 1, fy: y - 1, dx: x + 1, dy: y },
      { fx: x + 1, fy: y + 3, dx: x + 1, dy: y + 2 },
      { fx: x - 1, fy: y + 1, dx: x, dy: y + 1 },
      { fx: x + 3, fy: y + 1, dx: x + 2, dy: y + 1 }
    ].filter((f) => grid[f.fy]?.[f.fx] === '.')
    const face = faces[0]
    if (!face) continue
    grid[y + 1]![x + 1] = '.'
    grid[face.dy]![face.dx] = 'X'
    pickups.push({ type: 'treasure', x: x + 1, y: y + 1 })
    if (rng.chance(0.5)) return
    return
  }
}

const SECTOR_WORDS = ['SUBLEVEL', 'ANNEX', 'BLOCK', 'SECTOR', 'WING', 'VAULT', 'CONDUIT', 'RESERVE', 'HOLDING', 'OVERFLOW']

function procName(rng: RNG, floor: number): string {
  return `${rng.pick(SECTOR_WORDS)} ${floor}`
}
